import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, ArrowLeft, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const AdminSumberDana = () => {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [userRole, setUserRole] = useState('admin');
  const [formData, setFormData] = useState({
    type: 'bank',
    name: '',
    bank_name: '',
    account_number: '',
    account_holder: '',
    opening_balance: '0',
    is_active: true,
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (!token || !userStr) {
      navigate('/admin/login');
      return;
    }
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin' && user.role !== 'kasir') {
        navigate('/admin/login');
        return;
      }
      setUserRole(user.role);
    } catch {
      navigate('/admin/login');
      return;
    }
    loadSources();
  }, [navigate]);

  const loadSources = async () => {
    try {
      const response = await api.get('/admin/funding-sources');
      setSources(Array.isArray(response.data) ? response.data : []);
    } catch {
      toast.error('Gagal memuat sumber dana');
      setSources([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingSource(null);
    setFormData({
      type: 'bank',
      name: '',
      bank_name: '',
      account_number: '',
      account_holder: '',
      opening_balance: '0',
      is_active: true,
    });
  };

  const openCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEdit = (source) => {
    setEditingSource(source);
    setFormData({
      type: source.type || 'bank',
      name: source.name || '',
      bank_name: source.bank_name || '',
      account_number: source.account_number || '',
      account_holder: source.account_holder || '',
      opening_balance: String(source.opening_balance ?? 0),
      is_active: !!source.is_active,
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const parsedOpening = Number(String(formData.opening_balance ?? '').replace(/[^0-9.-]/g, ''));
      if (!Number.isFinite(parsedOpening)) {
        toast.error('Saldo awal tidak valid');
        return;
      }
      const payload = {
        ...formData,
        type: String(formData.type || '').toLowerCase(),
        name: String(formData.name || '').trim(),
        bank_name: formData.type === 'bank' ? String(formData.bank_name || '').trim() : null,
        account_number: formData.type === 'bank' ? String(formData.account_number || '').trim() : null,
        account_holder: formData.type === 'bank' ? String(formData.account_holder || '').trim() : null,
        opening_balance: parsedOpening,
      };
      if (editingSource) {
        await api.put(`/admin/funding-sources/${editingSource.id}`, payload);
        toast.success('Sumber dana berhasil diupdate');
      } else {
        await api.post('/admin/funding-sources', payload);
        toast.success('Sumber dana berhasil ditambahkan');
      }
      setShowDialog(false);
      resetForm();
      loadSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan sumber dana');
    }
  };

  const handleDelete = async (sourceId) => {
    if (!window.confirm('Yakin ingin menghapus sumber dana ini?')) return;
    try {
      await api.delete(`/admin/funding-sources/${sourceId}`);
      toast.success('Sumber dana berhasil dihapus');
      loadSources();
    } catch {
      toast.error('Gagal menghapus sumber dana');
    }
  };

  const isBank = String(formData.type || '').toLowerCase() === 'bank';
  const isAdmin = userRole === 'admin';
  const formatCurrency = (value) => {
    const n = Number(value || 0);
    const v = Number.isFinite(n) ? n : 0;
    return v.toLocaleString('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 });
  };
  const homePath = userRole === 'kasir' ? '/kasir/dashboard' : '/admin/dashboard';

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to={homePath} className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Admin Panel</span>
            </Link>
            <Link to={homePath} className="text-zinc-400 hover:text-primary flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Dashboard</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold italic uppercase text-white mb-2">Sumber Dana</h1>
              <p className="text-zinc-400">Kelola akun bank dan uang cash</p>
            </div>
            {isAdmin ? (
              <button
                onClick={openCreate}
                className="bg-primary text-black px-6 py-3 rounded-md font-bold uppercase tracking-wide hover:bg-primary-hover transition-all flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Tambah
              </button>
            ) : null}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sources.map((s) => (
                <div key={s.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-8 h-8 text-primary" />
                      <div>
                        <h3 className="text-xl font-bold text-white">{s.name || '-'}</h3>
                        <div className="flex gap-2 mt-1">
                          <span className="inline-block px-2 py-1 rounded-full text-xs font-bold bg-zinc-800 text-zinc-200">
                            {String(s.type || '').toLowerCase() === 'cash' ? 'CASH' : 'BANK'}
                          </span>
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${
                              s.is_active ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-zinc-700 text-zinc-400'
                            }`}
                          >
                            {s.is_active ? 'Aktif' : 'Nonaktif'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {String(s.type || '').toLowerCase() === 'bank' ? (
                    <div className="space-y-2 mb-4">
                      <div>
                        <div className="text-xs text-zinc-400">Bank</div>
                        <div className="text-white font-bold">{s.bank_name || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-zinc-400">Nomor Rekening</div>
                        <div className="text-2xl font-mono font-bold text-primary">{s.account_number || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-zinc-400">Atas Nama</div>
                        <div className="text-white font-bold">{s.account_holder || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-zinc-400">Saldo Awal</div>
                        <div className="text-white font-bold">{formatCurrency(s.opening_balance)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 mb-4">
                      <div className="text-sm text-zinc-300">Sumber dana cash.</div>
                      <div>
                        <div className="text-xs text-zinc-400">Saldo Awal</div>
                        <div className="text-white font-bold">{formatCurrency(s.opening_balance)}</div>
                      </div>
                    </div>
                  )}

                  {isAdmin ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(s)}
                        className="flex-1 bg-transparent border-2 border-primary text-primary hover:bg-primary/10 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center justify-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(s.id)}
                        className="flex-1 bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center justify-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Hapus
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {isAdmin ? (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold uppercase">
                {editingSource ? 'Edit Sumber Dana' : 'Tambah Sumber Dana'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Tipe</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 transition-all outline-none"
                >
                  <option value="bank">Bank</option>
                  <option value="cash">Cash</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Nama Sumber Dana</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                  placeholder="Contoh: BCA Operasional / Kas"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Saldo Awal</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.opening_balance}
                  onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                  placeholder="0"
                />
              </div>

              {isBank && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">Nama Bank</label>
                    <input
                      type="text"
                      required
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                      placeholder="BCA, Mandiri, dll"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">Nomor Rekening</label>
                    <input
                      type="text"
                      required
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                      placeholder="1234567890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-zinc-300 mb-2">Atas Nama</label>
                    <input
                      type="text"
                      required
                      value={formData.account_holder}
                      onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                      className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 px-4 placeholder:text-zinc-600 transition-all outline-none"
                      placeholder="Nama pemilik rekening"
                    />
                  </div>
                </>
              )}

              <div className="flex items-center gap-3 pt-2">
                <input
                  id="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4"
                />
                <label htmlFor="is_active" className="text-sm text-zinc-300 font-semibold">
                  Aktif
                </label>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full bg-primary text-black hover:bg-primary/90 font-bold uppercase rounded-md h-12 transition-all"
                >
                  Simpan
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
};

export default AdminSumberDana;
